import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Database, Plus } from '@phosphor-icons/react';
import { apiPath } from '../../config/apiBase';
import { notifySuccess, notifyError } from '../../lib/notify';
import {
  API,
  authHeaders,
  btnPrimary,
  btnSecondary,
  fieldStyle,
  labelStyle,
} from '../tickets/api/ticketsApi';
import DataSourceEditorForm from '../tickets/components/DataSourceEditorForm';

const DS = `${API}/data-sources`;
const CONN = `${DS}/connections`;
const LOC = apiPath('/api/tickets/data-sources/locations');

const blankSource = (productId) => ({
  name: '',
  product_id: productId || '',
  type: 'static',
  connection_id: '',
  url_template: '',
  method: 'GET',
  array_path: '',
  value_key: 'value',
  label_key: 'label',
  static_options: [],
  static_groups: {},
  _staticOptions: [],
  _staticMode: 'flat',
  _groups: [],
});

/**
 * Product → Data catalog tab.
 * Same form/table Data Source editor as the Data Sources module, scoped to this product.
 * Existing catalog values live in product-scoped data_sources — loaded as-is (no data loss).
 */
export default function ProductLocationsPanel({ productId, productName }) {
  const [sources, setSources] = useState([]);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [srcForm, setSrcForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState({ id: null, params: '{}', result: null, err: null });
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!productId) return;
    setLoading(true);
    setError(null);
    try {
      // Bootstrap legacy catalog types → data_sources (idempotent; keeps mappings).
      await axios.get(
        `${LOC}/${encodeURIComponent(productId)}/catalog`,
        authHeaders(),
      ).catch(() => null);

      const s = await axios.get(DS, {
        ...authHeaders(),
        params: { product_id: productId },
      });
      setSources(s.data || []);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to load product data sources';
      setError(typeof msg === 'string' ? msg : 'Failed to load');
      notifyError(typeof msg === 'string' ? msg : 'Failed to load');
      setSources([]);
    } finally {
      setLoading(false);
    }
    try {
      const c = await axios.get(CONN, authHeaders());
      setConnections(c.data || []);
    } catch {
      setConnections([]);
    }
  }, [productId]);

  useEffect(() => {
    setSrcForm(null);
    load();
  }, [load]);

  const openEdit = (s) => {
    const grouped = s.static_groups && Object.keys(s.static_groups).length > 0;
    const isBranchReporters = s.source_kind === 'branch_reporters'
      || (s.group_key_fields || []).join(',') === 'group,company,branch';
    setSrcForm({
      ...blankSource(productId),
      ...s,
      product_id: productId,
      _staticOptions: (s.static_options || []).map((o) => ({
        value: o.value || '',
        label: o.label || o.value || '',
      })),
      _staticMode: isBranchReporters ? 'branch_reporters' : (grouped ? 'grouped' : 'flat'),
      _groups: grouped
        ? Object.entries(s.static_groups).map(([key, opts]) => ({
          key,
          options: (opts || []).map((o) => ({
            value: o.value || '',
            label: o.label || o.value || '',
            contact_no: o.contact_no || '',
            email: o.email || '',
          })),
        }))
        : [],
    });
    setError(null);
  };

  const saveSrc = async () => {
    if (!srcForm?.name?.trim()) {
      notifyError('Name is required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const { _staticOptions, _staticMode, _groups, ...rest } = srcForm;
      const payload = {
        ...rest,
        name: rest.name.trim(),
        product_id: productId,
        static_options: [],
        static_groups: {},
        group_key_fields: rest.group_key_fields || [],
        source_kind: rest.source_kind || null,
      };
      if (rest.type === 'static') {
        if (_staticMode === 'branch_reporters') {
          const sg = {};
          (_groups || []).forEach((g) => {
            const k = (g.key || '').trim();
            if (!k) return;
            sg[k] = (g.options || [])
              .filter((o) => (o.value || '').trim())
              .map((o) => ({
                value: o.value.trim(),
                label: (o.label || o.value).trim(),
                contact_no: (o.contact_no || '').trim() || undefined,
                email: (o.email || '').trim() || undefined,
              }));
          });
          payload.static_groups = sg;
          payload.group_key_fields = ['group', 'company', 'branch'];
          payload.source_kind = 'branch_reporters';
        } else if (_staticMode === 'grouped') {
          const sg = {};
          (_groups || []).forEach((g) => {
            const k = (g.key || '').trim();
            if (!k) return;
            sg[k] = (g.options || [])
              .filter((o) => (o.value || '').trim())
              .map((o) => ({
                value: o.value.trim(),
                label: (o.label || o.value).trim(),
              }));
          });
          payload.static_groups = sg;
          // Preserve catalog:* kind so Form Builder / catalog mapping stays intact
          if (!(payload.source_kind || '').startsWith('catalog:')) {
            payload.source_kind = payload.source_kind || null;
          }
          payload.group_key_fields = [];
        } else {
          payload.static_options = (_staticOptions || [])
            .filter((o) => (o.value || '').trim())
            .map((o) => ({
              value: o.value.trim(),
              label: (o.label || o.value).trim(),
            }));
          if (!(payload.source_kind || '').startsWith('catalog:')) {
            payload.source_kind = payload.source_kind || null;
          }
          payload.group_key_fields = [];
        }
      }
      if (payload.data_source_id) {
        await axios.put(`${DS}/${payload.data_source_id}`, payload, authHeaders());
      } else {
        await axios.post(DS, payload, authHeaders());
      }
      setSrcForm(null);
      notifySuccess('Data source saved');
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail || 'Save failed';
      setError(typeof detail === 'string' ? detail : 'Save failed');
      notifyError(typeof detail === 'string' ? detail : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const delSrc = async (id) => {
    if (!window.confirm('Delete this data source? Forms bound to it will lose their options.')) return;
    try {
      await axios.delete(`${DS}/${id}`, authHeaders());
      notifySuccess('Data source deleted');
      await load();
    } catch (e) {
      notifyError(e.response?.data?.detail || 'Delete failed');
    }
  };

  const exportExcelOne = async (s) => {
    if (s.type !== 'static') {
      notifyError('Excel export is only for static data sources');
      return;
    }
    try {
      const r = await axios.get(`${DS}/${s.data_source_id}/excel/export`, {
        ...authHeaders(),
        responseType: 'blob',
      });
      const blob = new Blob([r.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${s.name || s.data_source_id}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      notifySuccess('Excel exported');
    } catch (e) {
      notifyError(e.response?.data?.detail || 'Excel export failed');
    }
  };

  const runPreview = async (id) => {
    setPreview((p) => ({ ...p, id, result: null, err: null }));
    let params = {};
    try {
      params = JSON.parse(preview.params || '{}');
    } catch {
      setPreview((p) => ({ ...p, err: 'Params must be valid JSON' }));
      return;
    }
    try {
      const r = await axios.post(`${DS}/resolve`, { data_source_id: id, params }, authHeaders());
      setPreview((p) => ({ ...p, result: r.data, err: null }));
    } catch (e) {
      setPreview((p) => ({ ...p, err: e.response?.data?.detail || 'Resolve failed' }));
    }
  };

  const optionSummary = (s) => {
    if (s.type !== 'static') return s.url_template || '';
    if (s.static_groups && Object.keys(s.static_groups).length) {
      const groups = Object.keys(s.static_groups).length;
      const opts = Object.values(s.static_groups).reduce((n, a) => n + (a?.length || 0), 0);
      return `${groups} groups · ${opts} options`;
    }
    return `${(s.static_options || []).length} options`;
  };

  const kindLabel = (s) => {
    const sk = s.source_kind || '';
    if (sk.startsWith('catalog:')) return 'catalog';
    if (sk === 'branch_reporters') return 'reporters';
    if (sk === 'groups' || sk === 'companies' || sk === 'branches') return sk;
    return null;
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 14 }}>
        <Database size={20} color="#374151" style={{ marginTop: 2 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>
            Data sources for {productName || productId}
          </div>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: '#6B7280', lineHeight: 1.45 }}>
            Same editor as the Data Sources module. Existing catalog values are stored in these
            product data sources — edit or import Excel without losing form bindings
            (<code>data_source_id</code> stays the same).
          </p>
        </div>
      </div>

      {error && (
        <div style={{
          background: '#FEF2F2',
          border: '1px solid #FECACA',
          color: '#B91C1C',
          borderRadius: 8,
          padding: '8px 12px',
          fontSize: 13,
          marginBottom: 12,
        }}
        >
          {error}
        </div>
      )}

      {srcForm ? (
        <DataSourceEditorForm
          form={srcForm}
          setForm={setSrcForm}
          products={[{ product_id: productId, name: productName || productId }]}
          connections={connections}
          onSave={saveSrc}
          onCancel={() => setSrcForm(null)}
          saving={saving}
          lockProductId={productId}
          hideProductSelect
        />
      ) : (
        <>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            <button
              type="button"
              style={btnPrimary}
              onClick={() => setSrcForm(blankSource(productId))}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Plus size={14} weight="bold" />
                New data source
              </span>
            </button>
            <button type="button" style={btnSecondary} onClick={load} disabled={loading}>
              {loading ? 'Loading…' : 'Refresh'}
            </button>
          </div>

          {loading && (
            <div style={{ color: '#9CA3AF', fontSize: 13, padding: 16 }}>Loading data sources…</div>
          )}

          {!loading && sources.length === 0 && (
            <div style={{
              border: '1px dashed #D1D5DB',
              borderRadius: 12,
              padding: 24,
              textAlign: 'center',
              color: '#9CA3AF',
              fontSize: 13,
            }}
            >
              No data sources for this product yet. Create one or import Excel from the editor.
            </div>
          )}

          <div style={{ display: 'grid', gap: 10 }}>
            {sources.map((s) => {
              const kind = kindLabel(s);
              return (
                <div key={s.data_source_id} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={row}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
                        <b style={{ fontSize: 13 }}>{s.name}</b>
                        <span style={pill}>{s.type}</span>
                        {kind && <span style={pillMuted}>{kind}</span>}
                      </div>
                      <div style={{ color: '#6B7280', fontSize: 12, marginTop: 2 }}>
                        {optionSummary(s)}
                      </div>
                      <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>
                        {s.data_source_id}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button type="button" style={btnSecondary} onClick={() => runPreview(s.data_source_id)}>Test</button>
                      <button type="button" style={btnSecondary} onClick={() => openEdit(s)}>Edit</button>
                      {s.type === 'static' && (
                        <button type="button" style={btnSecondary} onClick={() => exportExcelOne(s)}>Excel</button>
                      )}
                      <button
                        type="button"
                        style={{ ...btnSecondary, color: '#DC2626' }}
                        onClick={() => delSrc(s.data_source_id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  {preview.id === s.data_source_id && (
                    <div style={{
                      background: '#F9FAFB',
                      border: '1px solid #E5E7EB',
                      borderRadius: 12,
                      padding: 10,
                    }}
                    >
                      <label style={labelStyle}>Test params (JSON)</label>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <input
                          style={fieldStyle}
                          value={preview.params}
                          onChange={(e) => setPreview({ ...preview, params: e.target.value })}
                        />
                        <button type="button" style={btnSecondary} onClick={() => runPreview(s.data_source_id)}>Run</button>
                      </div>
                      {preview.err && (
                        <div style={{ color: '#DC2626', fontSize: 12, marginTop: 6 }}>{preview.err}</div>
                      )}
                      {preview.result && (
                        <div style={{ fontSize: 12, marginTop: 6 }}>
                          {preview.result.length} option(s):{' '}
                          {preview.result.slice(0, 8).map((o) => o.label).join(', ')}
                          {preview.result.length > 8 ? '…' : ''}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

const row = {
  border: '1px solid #E5E7EB',
  borderRadius: 12,
  padding: '10px 12px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: 12,
  background: 'white',
};
const pill = {
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 999,
  background: '#EFF6FF',
  color: '#1D4ED8',
  fontSize: 11,
  fontWeight: 600,
};
const pillMuted = {
  ...pill,
  background: '#F3F4F6',
  color: '#4B5563',
};
